import { useEffect } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { INACTIVE_PACKAGE_SUFFIX, packagesForPicker } from '../../lib/packageOptions';
import { Can } from '../../rbac/RequirePermission';
import { DailyWelcomeBonusCard } from './DailyWelcomeBonusCard';
import { NewUserFreeCreditsCard } from './NewUserFreeCreditsCard';

interface OfferConfig {
  enabled: boolean;
  maxPerDay: number;
  minIntervalHours: number;
  targets: string[];
  promoPackageId: string;
  headline: string;
  subtitle: string;
  newUserDays: number;
  inactiveDays: number;
  lowBalanceCoins: number;
}

interface CoinPkg {
  id: string;
  name: string;
  coins: number;
  bonusCoins: number;
  priceLabel: string;
  originalPriceLabel: string | null;
  discountPercent: number | null;
  isActive: boolean;
  displayOrder: number;
  amountPaise: string;
}

/** Daily offer / promo engine config (admin/super-admin). Applies immediately. */
export function OffersPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<OfferConfig>();
  const promoPackageId = Form.useWatch('promoPackageId', form);

  const cfg = useQuery({
    queryKey: ['admin', 'offers', 'config'],
    queryFn: async () => (await api.get<OfferConfig>('/admin/offers/config')).data,
  });
  const packages = useQuery({
    queryKey: ['admin', 'coin-packages'],
    queryFn: async () =>
      (await api.get<{ packages: CoinPkg[] }>('/admin/coin-packages')).data.packages,
  });

  useEffect(() => {
    if (cfg.data) form.setFieldsValue(cfg.data);
  }, [cfg.data, form]);

  const save = useMutation({
    mutationFn: async (v: OfferConfig) =>
      (await api.post<OfferConfig>('/admin/offers/config', v)).data,
    onSuccess: (data) => {
      message.success('Offer settings saved — applied immediately.');
      form.setFieldsValue(data);
      qc.invalidateQueries({ queryKey: ['admin', 'offers', 'config'] });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed',
      ),
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Offers &amp; promotions
      </Typography.Title>
      <Card title="Coin offer popup" loading={cfg.isLoading} style={{ maxWidth: 700 }}>
        <Form<OfferConfig> form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <Form.Item
            name="enabled"
            label="Offer popup enabled"
            valuePropName="checked"
            tooltip="Master switch for the daily coin-offer popup on app open."
          >
            <Switch />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item
              name="maxPerDay"
              label="Max displays / day"
              rules={[{ required: true, type: 'number', min: 1 }]}
            >
              <InputNumber min={1} max={20} />
            </Form.Item>
            <Form.Item
              name="minIntervalHours"
              label="Min interval (hours)"
              tooltip="Minimum gap between two popups for the same user (e.g. 12 or 24)."
              rules={[{ required: true, type: 'number', min: 0 }]}
            >
              <InputNumber min={0} max={168} />
            </Form.Item>
          </Space>
          <Form.Item
            name="targets"
            label="Target users"
            tooltip="Empty = everyone. Otherwise the user must match ANY selected segment."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Everyone"
              options={[
                { value: 'new', label: 'New users' },
                { value: 'inactive', label: 'Inactive users' },
                { value: 'low_balance', label: 'Low-balance users' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="promoPackageId"
            label="Promotional package"
            tooltip="The pack highlighted in the offer. Blank = auto (most popular / best bonus)."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Auto (popular / best bonus)"
              loading={packages.isLoading}
              options={packagesForPicker(packages.data ?? [], [promoPackageId]).map((p) => ({
                value: p.id,
                label: `${p.name} · ${p.coins} paid + ${p.bonusCoins} bonus · ${p.priceLabel}${
                  p.discountPercent != null ? ` (was ${p.originalPriceLabel}, ${p.discountPercent}% OFF)` : ''
                }${p.isActive ? '' : INACTIVE_PACKAGE_SUFFIX}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="headline" label="Headline">
            <Input maxLength={80} placeholder="Limited-time offer 🎁" />
          </Form.Item>
          <Form.Item name="subtitle" label="Subtitle">
            <Input maxLength={160} placeholder="Grab bonus coins before it's gone!" />
          </Form.Item>

          <Typography.Text type="secondary">Targeting thresholds</Typography.Text>
          <Space size="large" wrap style={{ marginTop: 8 }}>
            <Form.Item name="newUserDays" label="New ≤ (days)">
              <InputNumber min={0} max={60} />
            </Form.Item>
            <Form.Item name="inactiveDays" label="Inactive ≥ (days)">
              <InputNumber min={0} max={365} />
            </Form.Item>
            <Form.Item name="lowBalanceCoins" label="Low balance < (coins)">
              <InputNumber min={0} />
            </Form.Item>
          </Space>

          <Alert
            type="info"
            showIcon
            style={{ margin: '8px 0 16px' }}
            message="Applies within ~60s (config cache). The per-day cap + interval are enforced server-side per user (Redis)."
          />
          <Can permission="offers.edit">
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              Save offer settings
            </Button>
          </Can>
        </Form>
      </Card>
      <DailyWelcomeBonusCard />
      <NewUserFreeCreditsCard />
    </Space>
  );
}
